import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { RiErrorWarningLine, RiFilePdf2Line, RiRefreshLine, RiSearchLine } from '@remixicon/react';
import { AvatarNameCell, Badge, Button, EmptyState, SearchInput } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';

/**
 * Toolbar "Asboblar paneli" (577:14376): search 340×32, optional controls (e.g. "Filtrlash"), flexible space,
 * "Jami: N ta" neutral badge.
 */
export function RegistryToolbar({
  search,
  onSearch,
  placeholder,
  total,
  searchRef,
  children,
  aside,
}: {
  search: string;
  onSearch: (value: string) => void;
  placeholder: string;
  total: number | null;
  searchRef?: Ref<HTMLInputElement>;
  /** controls right after the search field */
  children?: ReactNode;
  /** controls before the total badge */
  aside?: ReactNode;
}) {
  const { t } = useTranslation('devices');
  return (
    <div className="flex min-h-32 shrink-0 items-center gap-10">
      <SearchInput
        ref={searchRef}
        className="w-340"
        value={search}
        onChange={(e) => onSearch(e.currentTarget.value)}
        onClear={() => onSearch('')}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && search) {
            e.stopPropagation();
            onSearch('');
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      {children}
      <div className="min-w-0 flex-1" />
      {aside}
      {total != null && (
        <Badge color="neutral" aria-live="polite">
          {t('common.total', { count: total })}
        </Badge>
      )}
    </div>
  );
}

/** "Qo'shgan xodim" cell: 20 px Dark blue initial avatar + name (577:13896); "—" without a creator. */
export function CreatorCell({ user }: { user: { fullName: string } | null | undefined }) {
  if (!user?.fullName) return <span className="text-fg-tertiary">—</span>;
  return <AvatarNameCell name={user.fullName} color="darkBlue" />;
}

/** Inline error banner of a list (03-modules §2.6): red tinted, "Ma'lumotlarni yuklab bo'lmadi" + "Qayta urinish". */
export function ErrorBanner({ onRetry, message, className }: { onRetry?: () => void; message?: ReactNode; className?: string }) {
  const { t } = useTranslation('devices');
  return (
    <div role="alert" className={cn('flex items-center gap-10 rounded-8 bg-tone-red-10 px-12 py-8 text-sm text-tone-red-lighter', className)}>
      <RiErrorWarningLine aria-hidden className="size-20 shrink-0" />
      <span className="min-w-0 flex-1">{message ?? t('common.loadError')}</span>
      {onRetry && (
        <Button size="sm" variant="secondary" leftIcon={RiRefreshLine} onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

/** Body of a table that matched nothing ("Hech narsa topilmadi" + "Tozalash"). */
export function NoResults({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation('devices');
  return (
    <EmptyState
      icon={RiSearchLine}
      title={t('common.notFound')}
      description={t('common.notFoundHint')}
      action={{ label: t('common.clear'), icon: null, onClick: onClear }}
    />
  );
}

/** Red tinted file badge "Pasport.pdf" (586:14946) — a button that opens the passport viewer. */
export function PassportBadge({ name, onOpen, label }: { name: string; onOpen?: () => void; label?: string }) {
  const { t } = useTranslation('devices');
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? 'pdf';
  const text = t('info.passportBadge', { ext });
  if (!onOpen) return <Badge color="red">{text}</Badge>;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={name}
      aria-label={label ?? `${t('info.viewPassport')}: ${name}`}
      className="cursor-pointer rounded-4 outline-none transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-2"
    >
      <Badge color="red">{text}</Badge>
    </button>
  );
}

/** Small file glyph tile used for non-image passports. */
export function FileGlyph({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('flex size-32 shrink-0 items-center justify-center rounded-8 bg-tone-red-20 text-tone-red', className)}>
      <RiFilePdf2Line className="size-18" />
    </span>
  );
}
